import { debounce } from 'lodash';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDevice, updateProps } from '../../../contextRedux/DeviceType/DeviceTypeModel';
import { SearchInput } from '../../../customComponents/input/SearchInput';
import { CustomTable } from '../../../customComponents/table/CustomTable';
import { RootState } from '../../../store';
import { DeviceColumn } from './DeviceColumn';

const DeviceTable: React.FC = () => {
  const dispatch = useDispatch();

  const reduxDevice = useSelector((state: RootState) => state.models?.deviceType);
  const devices = useSelector((state: RootState) => state.store?.deviceType.data ?? []);

  const pageSizes = [10, 20, 30, 50];
  const currentPage = Math.floor(reduxDevice.props.skip / reduxDevice.props.take) + 1;

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      dispatch(updateProps({ field: 'searchTerm', value }));
      dispatch(updateProps({ field: 'skip', value: 0 }));
    }, 500),
    [dispatch]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const handleEdit = (record: any) => {
    dispatch(setDevice(record));
    dispatch(updateProps({ field: 'isModalVisible', value: true }));
  };

  const paginationConfig = {
    current: currentPage,
    pageSize: reduxDevice.props.take,
    total: reduxDevice.props.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updateProps({ field: 'skip', value: skipValue }));
      dispatch(updateProps({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updateProps({ field: 'skip', value: 0 }));
      dispatch(updateProps({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };

  const renderTextWithHover = (text: string) => {
    const maxLength = 100;
    if (typeof text === 'string' && text.length > maxLength) {
      return <span title={text}>{`${text.substring(0, maxLength)}...`}</span>;
    }
    return text;
  };

  const customColumns = DeviceColumn(handleEdit).map((col) => ({
    ...col,
    ellipsis: true,
    render: (text: any, record: any) => {
      if (col.render) {
        return col.render(record);
      } else if (
        col.dataIndex === 'name' ||
        col.dataIndex === 'producer' ||
        col.dataIndex === 'provider' ||
        col.dataIndex === 'model'
      ) {
        return renderTextWithHover(text);
      }
      return text;
    },
  }));

  return (
    <div>
      <SearchInput
        placeholder='Search...'
        defaultValue={reduxDevice.props.searchTerm}
        onChange={handleSearch}
        style={{ marginBottom: 10 }}
      />
      <CustomTable
        initialDataSource={devices ?? []}
        pagination={paginationConfig}
        columns={customColumns}
        rowKey='id'
      />
    </div>
  );
};

export default DeviceTable;
